/**
 * API に送る Accept-Language（いまの画面の言語）。api.ts の api() / downloadFile() が全部の呼び出しに付ける。
 *
 * WHY（2026-09-30 console/english-pass・E1）: サーバーは確認メールなどの言語を Accept-Language から選び、登録時の開発者の locale にも
 *   保存する（apps/server/src/modules/platform/routes.ts の signup・recovery.ts → lib/mail/templates.ts の pickLocale）。
 *   ブラウザの既定のままだと、日本語のブラウザでダッシュボードを English にして登録した人に日本語の確認メールが届いていた。
 *   画面で選んだ言語（i18n の locale・localStorage `kumo.console.locale`）をブラウザの既定より優先するため、fetch に明示して付ける。
 * WHY 1 語だけ（'en' / 'ja'）か: サーバーはどちらの言語も持っていて、候補を並べる必要が無い。また CORS の「安全なヘッダ」の値
 *   （英数字と空白 * , - . ; = だけ）に収まるので、ダッシュボードを別オリジンに置いてもサーバーの CORS の許可（allowHeaders）を足さずに通る。
 *
 * このファイルは import を持たない（`node --test` で型の除去だけで動かすため。featureToggle.ts と同じ決め事）。
 * Locale の型は i18n/index.ts（React を読む）から持ち込まず、ここで同じ形を書く。
 */

export type AcceptLocale = 'en' | 'ja';

/** 画面の言語 → Accept-Language の値。知らない値は付けない（null = ブラウザの既定に任せる） */
export function acceptLanguageFor(locale: string | null | undefined): AcceptLocale | null {
  return locale === 'en' || locale === 'ja' ? locale : null;
}

/**
 * ヘッダの組に Accept-Language を足した新しい組を返す。呼ぶ側がもう付けていれば（大文字小文字を問わず）そちらを優先する
 * （特定の呼び出しだけ別の言語で頼みたい時のため。いまはそういう呼び出しは無い）
 */
export function withAcceptLanguage(headers: Record<string, string>, locale: string | null | undefined): Record<string, string> {
  if (Object.keys(headers).some((k) => k.toLowerCase() === 'accept-language')) return headers;
  const lang = acceptLanguageFor(locale);
  return lang ? { ...headers, 'accept-language': lang } : headers;
}
